import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnChanges, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { TotalsPrefix } from '@cds/betting-offer';
import { GridViewGroupingConfiguration } from '@cds/betting-offer/grouping/grid-view';
import { sortOptions } from '@frontend/sports/betting-offer/feature/fixture-factories';
import {
    EventOption,
    EventOptionGroup,
    EventParticipant,
    ResultSorting,
    SetGameScore,
    SpecialChar,
    TennisScore,
    Tv2MarketParameters,
} from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { MarqueeConfig, OddsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { ComponentProxy } from '@frontend/sports/deferred/feature/component-loader';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { MarketType } from '@frontend/sports/types/components/content';
import { WINDOW } from '@frontend/vanilla/core';
import { entries, take } from 'lodash-es';
import { ReplaySubject, distinctUntilChanged, map, switchMap, tap } from 'rxjs';

import { ArrowType, DropdownOption, OptionType } from '../dropdown/models';
import { OptionGroupService } from '../event-model/helpers/option-group.service';
import { NavigationRouterService } from '../navigation/navigation-router.service';
import { HighlightsCardComponent } from './highlights-card.component';
import { BetNowButtonBinding, OptionGroupBinding } from './highlights-marquee-component-loader.service';
import { HighlightsMarqueePageService } from './highlights-marquee-page.service';
import { MarqueeHelperService } from './marquee-helper.service';
import { FixtureTile, Tile } from './marquee-tile.model';
import { MarqueeTrackingService } from './marquee-tracking.service';
import { MarqueeConstants } from './marquee.constants';

export interface Jersey {
    player1: string;
    player2: string;
    fallback: string;
}

interface ParticipantModel {
    participant: EventParticipant;
    player1Name: string;
    player2Name: string;
    singleParticipant: string;
}

@Component({
    selector: 'ms-highlights-regular-card',
    templateUrl: 'highlights-regular-card.component.html',
    styleUrls: ['highlights-regular-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class HighlightsRegularCardComponent extends HighlightsCardComponent<FixtureTile> implements OnInit, OnChanges {
    @Output() regularToCorrectScoreFallbackTile = new EventEmitter<{ tile: Tile; marketType: string }>();

    url: string;
    tileClass: string;
    truncationPlayer: string;
    overriddenOptionNames: string[];
    tileOptionGroup?: EventOptionGroup;
    dropDownOptionGroup?: EventOptionGroup;
    dropdownOptions?: DropdownOption[];
    activeDropDownOption?: EventOption;
    isSportExcludeSelectionLine = false;
    showDropDown = false;
    isDoublesPlayer = false;
    marketOverridenName: string | undefined;
    gridGroupId: string;
    isFallbackApplied: boolean;
    marqueeOptionGroupComponent: ComponentProxy<OptionGroupBinding>;
    betNowButtonComponent: ComponentProxy<BetNowButtonBinding>;
    participantModel: ParticipantModel[];

    tracking: TrackingSource;

    private readonly tile$ = new ReplaySubject<FixtureTile>(1);
    private readonly _window = inject(WINDOW);

    constructor(
        protected override urlHelper: UrlHelperService,
        protected navigationRouter: NavigationRouterService,
        protected optionGroupService: OptionGroupService,
        protected marqueeConstants: MarqueeConstants,
        protected sitecore: Sitecore,
        protected oddsConfig: OddsConfig,
        protected offerGroupingService: OfferGroupingService,
        protected override highlightsMarqueePageService: HighlightsMarqueePageService,
        protected marqueeConfig: MarqueeConfig,
        protected marqueeHelperService: MarqueeHelperService,
        protected router: Router,
        protected marqueeTrackingService: MarqueeTrackingService,
        protected changeDetectionRef: ChangeDetectorRef,
    ) {
        super('RegularMarquee', highlightsMarqueePageService, urlHelper);
        this.tile$
            .pipe(
                map((tile) => tile.sportId),
                distinctUntilChanged(),
                switchMap((sportId) =>
                    this.offerGroupingService.getGridGrouping(sportId).pipe(
                        tap((gridGroupConfig) => {
                            this.overriddenOptionNames = this.getOverriddenOptionNames(gridGroupConfig);
                            this.marqueeOptionGroupComponent?.update({
                                optionNames: this.overriddenOptionNames,
                            });
                            // getGridGrouping is an http result, patched by NgZone, it's sufficient to only mark
                            // the component as dirty in order to let change detection do its job
                            this.changeDetectionRef.markForCheck();
                        }),
                    ),
                ),
                takeUntilDestroyed(),
            )
            .subscribe();
    }

    ngOnChanges(changes: ISimpleChanges<HighlightsRegularCardComponent>): void {
        this.isSportExcludeSelectionLine = false;

        if (changes.tile) {
            if (changes.tile.firstChange) {
                this.url = !this.tile.isFromPortal ? this.urlHelper.getEventUrl(this.tile.event) : '';
            }
            this.tileOptionGroup = this.getOptionGroup();
            this.marketOverridenName = this.marqueeConfig.marqueeMarketOverrides[this.getMarketOverrideKey()] || this.tileOptionGroup?.name || '';
            this.isFallbackApplied = this.getIsFallbackApplied();

            if (this.tileOptionGroup) {
                const marketType = this.isFallbackApplied ? this.tile.fallbackMarketType : this.tile.marketType;

                if (this.tileOptionGroup && marketType && this.marqueeConfig.marketListWithOptionDropdown.indexOf(marketType.replace(' ', '')) > -1) {
                    this.showDropDown = true;
                    this.dropdownOptions = this.getDropdownOptions(this.tileOptionGroup.options);

                    if (
                        !this.activeDropDownOption ||
                        !this.tile.event.optionGroups[0].options.find((option) => option.name === this.activeDropDownOption?.name)
                    ) {
                        this.activeDropDownOption = this.tileOptionGroup.options.find(
                            (option) => option.name === (this.dropdownOptions ? this.dropdownOptions[0].name : this.tileOptionGroup?.options[0].name),
                        );
                    }

                    if (this.activeDropDownOption) {
                        this.getTileOptionGroupForDropDown(this.activeDropDownOption.name);
                    }
                } else {
                    this.showDropDown = false;
                }

                if (marketType === MarketType.CorrectScore || marketType === MarketType.DynamicCorrectScore) {
                    this.regularToCorrectScoreFallbackTile.emit({ tile: this.tile, marketType });
                }
            }
            this.marqueeOptionGroupComponent?.update({
                optionGroup: this.tileOptionGroup,
                showDropDown: this.showDropDown,
            });
            this.isDoublesPlayer = this.tile.event.participants.every((p) => p.name.includes(SpecialChar.Slash));

            this.participantModel = [
                {
                    participant: this.tile.event.participants[0],
                    player1Name: this.getParticipantName(0, 0),
                    player2Name: this.getParticipantName(0, 1),
                    singleParticipant: this.getParticipantName(0),
                },
                {
                    participant: this.tile.event.participants[1],
                    player1Name: this.getParticipantName(1, 0),
                    player2Name: this.getParticipantName(1, 1),
                    singleParticipant: this.getParticipantName(1),
                },
            ];

            this.isSportExcludeSelectionLine = this.oddsConfig.display.sportsToExcludeSelectionLineDisplay.includes(this.tile.sportId);
            this.tileClass = this.getTileClass();
            this.truncationPlayer = this.getTruncationPlayer();
            this.tracking = {
                source: this.trackingSource,
                position: this.trackingPosition,
                isFallbackMarketEnabled: this.isFallbackApplied,
                marqueeName: this.tile.trackingDetail?.marqueeName,
                sitecoreTemplateId: this.tile.trackingDetail?.sitecoreTemplateId,
                marqueeType: this.tile.source?.marqueeType,
                isAutomatedMarquee: this.tile.source?.isAutomatedMarquee,
            };

            this.tile$.next(this.tile);
        }
    }

    private getDropdownOptions(options: EventOption[]): DropdownOption[] {
        options = sortOptions(options, ResultSorting.Odds);
        const marketDropdownOptions = take(options, this.marqueeConfig.maxMarketsToShowInOptionDropdown).map((option) => this.mapOption(option));

        if (options.length > this.marqueeConfig.maxMarketsToShowInOptionDropdown) {
            marketDropdownOptions.push({
                id: 'seeMore',
                name: this.preventNavigation ? this.sitecore.marquee.EventPageDropDownMoreOptionsTitle : this.sitecore.globalMessages.SeeMore,
                type: OptionType.Special,
                arrowType: this.preventNavigation ? ArrowType.None : ArrowType.Right,
            });
        }

        return marketDropdownOptions;
    }

    private mapOption(option: EventOption): DropdownOption<EventOption> {
        return {
            id: option.id,
            name: option.name,
            type: OptionType.Regular,
        };
    }

    private getMarketOverrideKey(): string {
        if (this.tile.marketType === MarketType.DynamicMarket) {
            return this.tileOptionGroup?.templateId || '';
        }

        if (this.getIsFallbackApplied()) {
            return this.tile.fallbackMarketType?.toLowerCase() || '';
        }

        return this.tile.marketType?.toLowerCase() || '';
    }

    private getTileOptionGroupForDropDown(market: string | undefined): EventOptionGroup | undefined {
        const marketType = this.getIsFallbackApplied() ? this.tile.fallbackMarketType : this.tile.marketType;

        const marketGametemplate = this.marqueeConfig.mapMarketToGameTemplateIds.find(
            (i) => i.sportId === this.tile.sportId && i.market === marketType,
        );
        const marketTypeOptionGroup = this.tile.event.optionGroups.find(
            (p) =>
                marketGametemplate?.gameTemplateIds?.includes(parseInt(p.templateId)) ||
                p.parameters?.find((param) => param.key === Tv2MarketParameters.MarketType)?.value === marketGametemplate?.marketType,
        );
        const options = marketTypeOptionGroup!.options;
        const filteredOption = options.find((option: { name: string }) => option.name === market);

        this.dropDownOptionGroup = Object.assign(new EventOptionGroup(), marketTypeOptionGroup);

        this.dropDownOptionGroup.options = [filteredOption as EventOption];

        return this.dropDownOptionGroup;
    }

    optionSelected(option: DropdownOption): void {
        if (option.type === OptionType.Special) {
            if (this.preventNavigation) {
                return;
            }

            this.marqueeTrackingService.trackEventClicks(this.trackingPosition, this.tile, this.page, false);
            this.navigationRouter.resetNavigation();
            this.router.navigate([this.url]);
        } else {
            const optionGroup = this.getTileOptionGroupForDropDown(option.name);
            this.activeDropDownOption = optionGroup?.options[0];
        }
    }

    private getOptionGroup(): EventOptionGroup | undefined {
        const tileOptionGroup = this.marqueeHelperService.getOptionGroup(this.tile);
        this.gridGroupId = this.marqueeHelperService.gridGroupId;

        return tileOptionGroup;
    }

    tileClick(): void {
        this.navigationRouter.resetNavigation();
        if (this.tile.isFromPortal) {
            const eventUrl = this.urlHelper.getEventUrl(this.tile.event);
            this._window.parent.postMessage({ eventUrl }, '*');
        }
    }

    getTileClass(): string {
        const setBasedSports = entries(this.marqueeConstants.setBasedSports);
        if (setBasedSports.some((x) => +x[0] === this.tile.event.sport.id)) {
            const setsCountClassName = this.getSetsCountClassName();

            return this.tile.event.scoreboard.started ? 'card-tennis-live' + setsCountClassName : 'card-prematch card-tennis' + setsCountClassName;
        }

        return 'card-prematch card-football';
    }

    getTruncationPlayer(): string {
        const hasToTruncate = this.marqueeConstants.sportsToTruncateNameInOneRow.some((x) => x === this.tile.sportId);

        return hasToTruncate && !this.tile.event.scoreboard.started ? 'truncated-name-sport' : '';
    }

    getParticipantName(participantIndx: number, index?: number): string {
        const participantName = this.marqueeHelperService.getParticipantName(participantIndx, this.tile.event);
        if (index !== undefined && this.isDoublesPlayer) {
            return this.getDoublePlayerName(index, participantName);
        }

        return participantName;
    }

    private getDoublePlayerName(index: number, playerName: string): string {
        const playerNames = playerName.split('/');

        return playerNames[index];
    }

    private getSetsCountClassName(): string {
        const sets =
            this.tile.event.scoreboard.score instanceof TennisScore || this.tile.event.scoreboard.score instanceof SetGameScore
                ? this.tile.event.scoreboard.score.setCount
                : 0;

        return sets === 3 ? ' three-sets' : sets === 7 ? ' seven-sets' : '';
    }

    private getOverriddenOptionNames(gridGroupConfig: GridViewGroupingConfiguration | undefined): string[] {
        return this.tileOptionGroup?.options.map((option, i) => this.getOptionName(option, i, gridGroupConfig)) || [];
    }

    private getOptionName(option: EventOption, index: number, gridGroupConfig: GridViewGroupingConfiguration | undefined): string {
        let optionNames: string[] = [];
        if (this.tile.gridGroupIds?.length && this.gridGroupId && this.tileOptionGroup?.categoryAttribute && option.totalsPrefix) {
            const prefix =
                option.totalsPrefix === TotalsPrefix.Over ? this.sitecore.eventGrid.SixPack_TotalsOver : this.sitecore.eventGrid.SixPack_TotalsUnder;

            return `${prefix} ${this.tileOptionGroup.categoryAttribute}`;
        } else if (this.tile.gridGroupIds?.length && this.gridGroupId) {
            if (!this.isSportExcludeSelectionLine) {
                const marqueeGroupsOfMarkets = this.marqueeConfig.marqueeGroupsOfMarkets.find(
                    (i) => i.sportId === this.tile.sportId && i.gridGroupId === this.gridGroupId,
                );
                optionNames = marqueeGroupsOfMarkets?.optionNameOverride || [];
                if (!marqueeGroupsOfMarkets?.ignoreGroupingNames) {
                    optionNames = gridGroupConfig?.groups.find((group) => group.id === this.gridGroupId)?.optionNames || [];
                }

                if (optionNames.length > 0) {
                    return optionNames[index];
                }
                if (marqueeGroupsOfMarkets?.showOverriddenName) {
                    return option.overridenName || option.name;
                }
            }

            return option.optionAttribute || option.marketAttribute || '';
        }
        const marketType = this.getIsFallbackApplied() ? this.tile.fallbackMarketType : this.tile.marketType;

        const nonGridableOptionNames = this.marqueeConfig.mapMarketToGameTemplateIds.find(
            (i) => i.sportId === this.tile.sportId && i.market === marketType,
        );

        optionNames = nonGridableOptionNames?.optionNameOverride || [];

        if (optionNames.length > 0) {
            return optionNames[index];
        }
        const optionName = this.isSportExcludeSelectionLine ? option.optionAttribute! : option.overridenName || option.name;

        return optionName;
    }

    private getIsFallbackApplied(): boolean {
        return this.marqueeHelperService.fallbackFlagCollection[this.tile.fixtureId + this.tile.marketType];
    }
}
